import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { LogIn } from 'lucide-react';
import { validateEmail, validatePassword } from '../utils/formValidation';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const { signInWithEmail } = useAuth();
  const navigate = useNavigate();

  const runValidation = () => {
    const emailRes = validateEmail(email);
    const passRes = validatePassword(password);
    const errs: Record<string, string> = {};
    if (!emailRes.valid) errs.email = emailRes.message;
    if (!passRes.valid) errs.password = passRes.message;
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleBlur = (field: string) => {
    setTouched((p) => ({ ...p, [field]: true }));
    if (field === 'email') {
      const res = validateEmail(email);
      setFieldErrors((p) => ({ ...p, email: res.valid ? '' : res.message }));
    }
    if (field === 'password') {
      const res = validatePassword(password);
      setFieldErrors((p) => ({ ...p, password: res.valid ? '' : res.message }));
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!runValidation()) return;
    setLoading(true);

    const { error } = await signInWithEmail(email, password);
    
    if (error) {
      setError(error.message || 'ההתחברות נכשלה');
      setLoading(false);
    } else {
      navigate('/dashboard');
    }
  };

  const isFormValid = validateEmail(email).valid && validatePassword(password).valid;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-12 h-12 bg-black rounded-lg mb-4">
            <LogIn className="w-6 h-6 text-white" />
          </div>
          <h1 className="text-2xl font-semibold text-gray-900 mb-2">ברוכים הבאים</h1>
          <p className="text-gray-600">התחברו לחשבון שלכם</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-lg border border-gray-200 p-4 sm:p-8 shadow-sm">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <div className="mb-4">
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5">
              אימייל
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={() => handleBlur('email')}
              required
              className={`w-full px-3 py-2.5 border rounded-md focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent min-h-[44px] text-base ${
                touched.email && fieldErrors.email ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="you@example.com"
            />
            {touched.email && fieldErrors.email && (
              <p className="mt-1 text-sm text-red-600">{fieldErrors.email}</p>
            )}
          </div>

          <div className="mb-6">
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1.5">
              סיסמה
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onBlur={() => handleBlur('password')}
              required
              className={`w-full px-3 py-2.5 border rounded-md focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent min-h-[44px] text-base ${
                touched.password && fieldErrors.password ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="••••••••"
            />
            {touched.password && fieldErrors.password && (
              <p className="mt-1 text-sm text-red-600">{fieldErrors.password}</p>
            )}
          </div>

          <button
            type="submit"
            disabled={loading || !isFormValid}
            className="w-full bg-black text-white py-2.5 px-4 rounded-md font-medium hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors min-h-[44px] text-base"
          >
            {loading ? 'מתחבר...' : 'התחברות'}
          </button>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              אין לכם חשבון?{' '}
              <Link to="/signup" className="text-black font-medium hover:underline">
                הרשמה
              </Link>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}
